import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Membership: React.FC = () => {
  const [formData, setFormData] = useState({
    title: 'Mr',
    firstName: '',
    lastName: '',
    gender: 'Male',
    phone: '',
    email: '',
    emergencyContact: '',
    address: '',
    city: '',
    postcode: '',
    interests: [] as string[],
    activities: [] as string[]
  });
  const [retrieveEmail, setRetrieveEmail] = useState('');
  const [memberId, setMemberId] = useState('');
  const [message, setMessage] = useState('');
  const [showRetrieve, setShowRetrieve] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (category: 'interests' | 'activities', value: string) => {
    setFormData(prev => {
      const currentArray = prev[category];
      if (currentArray.includes(value)) {
        return {
          ...prev,
          [category]: currentArray.filter(item => item !== value)
        };
      } else {
        return {
          ...prev,
          [category]: [...currentArray, value]
        };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(''); // Clear any previous messages
    
    try {
      const response = await axios.post(`${API_URL}/members`, formData);
      if (response.data.success && response.data.data) {
        const memberId = response.data.data.memberId;
        const userEmail = formData.email;
        
        setMessage(`Thank you for signing up! Your Member ID is ${memberId}. We have also sent this to your email address ${userEmail}.`);
        
        // Reset form
        setFormData({
          title: 'Mr',
          firstName: '',
          lastName: '',
          gender: 'Male',
          phone: '',
          email: '',
          emergencyContact: '',
          address: '',
          city: '',
          postcode: '',
          interests: [],
          activities: []
        });
        
        // Scroll to success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      const errorMsg = error.response?.data?.message || 'Error registering member. Please try again.';
      setMessage(errorMsg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleRetrieve = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await axios.get(`${API_URL}/members/retrieve?email=${retrieveEmail}`);
      if (response.data.success) {
        setMemberId(response.data.data.memberId);
        setMessage(`Your Member ID is: ${response.data.data.memberId}`);
      }
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error retrieving member ID');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Membership Registration</h1>
          <p className="text-lg text-gray-600">
            Join our community of 500+ members and gain access to all our activities
          </p>
        </div>

        {/* Toggle Buttons */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={() => setShowRetrieve(false)}
            className={`px-6 py-3 rounded-lg font-semibold transition duration-300 ${
              !showRetrieve ? 'bg-primary text-white' : 'bg-white text-primary border-2 border-primary'
            }`}
          >
            New Registration
          </button>
          <button
            onClick={() => setShowRetrieve(true)}
            className={`px-6 py-3 rounded-lg font-semibold transition duration-300 ${
              showRetrieve ? 'bg-primary text-white' : 'bg-white text-primary border-2 border-primary'
            }`}
          >
            Retrieve Member ID
          </button>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.includes('Error') || message.includes('No member') 
              ? 'bg-red-100 text-red-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {message}
          </div>
        )}

        {showRetrieve ? (
          /* Retrieve Member ID Form */
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold mb-6">Retrieve Your Member ID</h2>
            <form onSubmit={handleRetrieve}>
              <div className="mb-4">
                <label className="label">Email Address</label>
                <input
                  type="email"
                  value={retrieveEmail}
                  onChange={(e) => setRetrieveEmail(e.target.value)}
                  className="input-field"
                  required
                />
              </div>
              <button type="submit" className="btn-primary w-full">
                Retrieve Member ID
              </button>
            </form>
          </div>
        ) : (
          /* Registration Form */
          <div className="bg-white rounded-lg shadow-md p-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title */}
                <div>
                  <label className="label">Title *</label>
                  <select
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="input-field"
                    required
                  >
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Miss">Miss</option>
                    <option value="Ms">Ms</option>
                    <option value="Dr">Dr</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Gender */}
                <div>
                  <label className="label">Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="input-field"
                    required
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                {/* First Name */}
                <div>
                  <label className="label">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="label">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="label">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                {/* Emergency Contact */}
                <div className="md:col-span-2">
                  <label className="label">Emergency Contact</label>
                  <input
                    type="text"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Name and phone number (optional)"
                  />
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="label">Address *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="input-field"
                    rows={2}
                    required
                  />
                </div>

                {/* City */}
                <div>
                  <label className="label">City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                {/* Postcode */}
                <div>
                  <label className="label">Postcode *</label>
                  <input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>
                
                {/* Interests Section */}
                <div className="md:col-span-2 border-t pt-6 mt-4">
                  <h3 className="text-lg font-semibold mb-2">Interests</h3>
                  <p className="text-sm text-gray-600 mb-4">Select all that apply:</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {['Walking', 'Running', 'Cycling', 'Badminton', 'Football', 'Fitness', 'Health', 'Weight Loss', 'Fun', 'Yoga', 'Zumba', 'Aerobics', 'Swimming', 'Canoeing', 'Snooker/Pool'].map((interest) => (
                      <label key={interest} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.interests.includes(interest)}
                          onChange={() => handleCheckboxChange('interests', interest)}
                          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                        />
                        <span className="text-sm">{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Activities Section */}
                <div className="md:col-span-2 border-t pt-6 mt-4">
                  <h3 className="text-lg font-semibold mb-2">Which activities are you interested in doing?</h3>
                  <p className="text-sm text-gray-600 mb-4">Select all that apply:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {['Walk and Talk', 'Cycling', 'Badminton', 'Dads and Lads Football', 'Adults Football', 'Aerobics', 'Canoeing'].map((activity) => (
                      <label key={activity} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.activities.includes(activity)}
                          onChange={() => handleCheckboxChange('activities', activity)}
                          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                        />
                        <span className="text-sm">{activity}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <button type="submit" className="btn-primary w-full">
                  Register as Member
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Membership;
